import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useModuleAssignments,
  useAssignmentWithQuestions,
  useUserSubmission,
  useSubmitAssignment,
  AssignmentQuestion,
} from "@/hooks/useAssignments";
import { FileText, Loader2, Save, CheckCircle2, Clock, MessageSquare, Upload, FileIcon, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { openSignedStorageUrl } from "@/lib/storage";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  EvidenceEditor, EvidenceViewer, useChallengeEvidence, useTopShopChallengeContext,
} from "@/components/topshop/TopShopChallengeEvidence";

interface AssignmentsSectionProps {
  moduleId: string;
  onSubmitSuccess?: () => void;
  selectedAssignmentId?: string | null;
}

export function AssignmentsSection({ moduleId, onSubmitSuccess, selectedAssignmentId }: AssignmentsSectionProps) {
  const { data: assignments, isLoading } = useModuleAssignments(moduleId);
  
  const publishedAssignments = assignments?.filter(a => a.is_published) || [];

  // Determine which accordion item should be open by default
  const defaultAccordionValue = selectedAssignmentId && publishedAssignments.some(a => a.id === selectedAssignmentId)
    ? selectedAssignmentId
    : undefined;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (publishedAssignments.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No assignments available for this module</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Accordion type="single" collapsible className="w-full" defaultValue={defaultAccordionValue}>
        {publishedAssignments.map((assignment) => (
          <AssignmentCard key={assignment.id} assignmentId={assignment.id} onSubmitSuccess={onSubmitSuccess} />
        ))}
      </Accordion>
    </div>
  );
}

function AssignmentCard({ assignmentId, onSubmitSuccess }: { assignmentId: string; onSubmitSuccess?: () => void }) {
  const { user } = useAuth();
  const userId = user?.id;
  const { data: assignment, isLoading } = useAssignmentWithQuestions(assignmentId);
  const { data: submission } = useUserSubmission(assignmentId);
  const submitMutation = useSubmitAssignment();
  
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [missingRequired, setMissingRequired] = useState<string[]>([]);
  const [confirmNoEvidence, setConfirmNoEvidence] = useState(false);

  // Top Shop challenge: evidence, lock while with the judges
  const { data: topShop } = useTopShopChallengeContext(assignmentId);
  const isTopShop = !!topShop?.focusAreaId;
  const tsApplicationId = topShop?.applicationId ?? null;
  const { data: evidence = [] } = useChallengeEvidence(tsApplicationId, topShop?.focusAreaId);
  const tsLocked = isTopShop && ["submitted", "passed", "failed"].includes(submission?.status ?? "");

  // Initialize answers from existing submission
  useEffect(() => {
    if (submission?.answers) {
      const existingAnswers: Record<string, string> = {};
      const existingFileUrls: Record<string, string> = {};
      submission.answers.forEach((answer) => {
        existingAnswers[answer.question_id] = answer.answer_text || "";
        if (answer.file_url) {
          existingFileUrls[answer.question_id] = answer.file_url;
        }
      });
      setAnswers(existingAnswers);
      setFileUrls(existingFileUrls);
      setHasChanges(false);
    }
  }, [submission]);

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setHasChanges(true);
  };

  const handleMultipleCorrectChange = (questionId: string, optionId: string, checked: boolean) => {
    const currentAnswer = answers[questionId] || "";
    const selectedOptions = currentAnswer ? currentAnswer.split(",").filter(Boolean) : [];
    
    if (checked) {
      selectedOptions.push(optionId);
    } else {
      const index = selectedOptions.indexOf(optionId);
      if (index > -1) selectedOptions.splice(index, 1);
    }
    
    handleAnswerChange(questionId, selectedOptions.join(","));
  };

  const handleFileUpload = async (questionId: string, file: File) => {
    try {
      setUploading(prev => ({ ...prev, [questionId]: true }));
      
      if (!userId) {
        toast.error("You must be signed in to upload a file.");
        return;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      // Path shape MUST be <user_id>/<assignment_id>/<file> — the storage
      // policies on the private homework-files bucket are folder-scoped.
      const filePath = `${userId}/${assignmentId}/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from("homework-files")
        .upload(filePath, file);
      
      if (uploadError) throw uploadError;
      
      // Store the path, not the full URL
      setFileUrls(prev => ({ ...prev, [questionId]: filePath }));
      setAnswers(prev => ({ ...prev, [questionId]: file.name }));
      setHasChanges(true);
      toast.success("File uploaded successfully");
    } catch (error: any) {
      toast.error("Failed to upload file: " + error.message);
    } finally {
      setUploading(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const removeFile = (questionId: string) => {
    setFileUrls(prev => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
    setAnswers(prev => ({ ...prev, [questionId]: "" }));
    setHasChanges(true);
  };

  const handleSubmit = (skipEvidenceCheck = false) => {
    if (tsLocked) return;
    // Enforce required questions. The red asterisk was rendered but never
    // checked, so a fully blank submission was accepted.
    const missing = (assignment?.questions ?? []).filter((q) => {
      if (!q.is_required) return false;
      const text = (answers[q.id] ?? "").trim();
      const file = fileUrls[q.id];
      return !text && !file;
    });

    if (missing.length > 0) {
      setMissingRequired(missing.map((q) => q.id));
      toast.error(
        missing.length === 1
          ? "Please answer the required question before submitting."
          : `Please answer all ${missing.length} required questions before submitting.`
      );
      return;
    }
    setMissingRequired([]);

    if (isTopShop && tsApplicationId && !skipEvidenceCheck && evidence.length === 0) {
      setConfirmNoEvidence(true);
      return;
    }

    // Combine answers with file URLs
    const submissionAnswers = { ...answers };
    Object.entries(fileUrls).forEach(([questionId, url]) => {
      submissionAnswers[`${questionId}__file`] = url;
    });
    
    submitMutation.mutate(
      { assignmentId, answers: submissionAnswers, fileUrls },
      {
        onSuccess: (result) => {
          setHasChanges(false);
          if (isTopShop && result?.submissionId) {
            // Never surface a notify failure; the submission already succeeded.
            supabase.functions
              .invoke("notify-admin-topshop-submission", { body: { submission_id: result.submissionId } })
              .catch(() => undefined);
          }
          onSubmitSuccess?.();
        },
      }
    );
  };

  const isSubmitted = !!submission;
  // Zero-question assignments, and any assignment not yet submitted, must be
  // submittable even though no answer has been edited.
  const questionCount = assignment?.questions?.length ?? 0;
  const canSubmitWithoutChanges = questionCount === 0 || !isSubmitted;
  const hasInstructorFeedback = !!submission?.instructor_feedback;

  if (isLoading) {
    return (
      <AccordionItem value={assignmentId}>
        <AccordionTrigger>
          <div className="flex items-center gap-3">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading...</span>
          </div>
        </AccordionTrigger>
      </AccordionItem>
    );
  }

  if (!assignment) return null;

  return (
    <AccordionItem value={assignmentId}>
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center gap-3">
          <FileText className="h-4 w-4 text-primary" />
          <span className="font-medium">{assignment.title}</span>
          {isSubmitted && (
            <Badge variant="secondary" className="ml-2">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Submitted
            </Badge>
          )}
          {hasInstructorFeedback && (
            <Badge variant="default" className="ml-1">
              <MessageSquare className="h-3 w-3 mr-1" />
              Feedback
            </Badge>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-4">
        <div className="space-y-6">
          {assignment.description && (
            <p className="text-muted-foreground">{assignment.description}</p>
          )}

          {hasInstructorFeedback && (
            <Alert>
              <MessageSquare className="h-4 w-4" />
              <AlertDescription>
                <strong>Instructor Feedback:</strong>
                <p className="mt-1 whitespace-pre-wrap">{submission?.instructor_feedback}</p>
                {submission?.instructor_reviewed_at && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Reviewed on {new Date(submission.instructor_reviewed_at).toLocaleDateString()}
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}

          {isSubmitted && !tsLocked && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>
                Last saved: {new Date(submission.updated_at).toLocaleString()}
              </span>
            </div>
          )}

          {tsLocked && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                {submission?.status === "submitted" ? "Ready for scoring. The judges will review it soon." : "Reviewed by the judges."}
              </AlertDescription>
            </Alert>
          )}

          <fieldset disabled={tsLocked} className="space-y-4">
            {assignment.questions?.map((question, index) => (
              <QuestionRenderer
                key={question.id}
                question={question}
                index={index}
                answer={answers[question.id] || ""}
                fileUrl={fileUrls[question.id]}
                uploading={uploading[question.id]}
                onAnswerChange={handleAnswerChange}
                onMultipleCorrectChange={handleMultipleCorrectChange}
                onFileUpload={handleFileUpload}
                onRemoveFile={removeFile}
                isMissingRequired={missingRequired.includes(question.id)}
              />
            ))}
          </fieldset>

          {isTopShop && tsApplicationId && topShop?.focusAreaId && (
            tsLocked ? (
              <div className="space-y-2 rounded-lg border p-4">
                <h4 className="font-semibold">Photos and links</h4>
                <EvidenceViewer items={evidence} />
              </div>
            ) : (
              <EvidenceEditor applicationId={tsApplicationId} focusAreaId={topShop.focusAreaId} items={evidence} />
            )
          )}

          {!tsLocked && (
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              {isSubmitted 
                ? "You can update your answers anytime."
                : "Your progress will be saved automatically."}
            </p>
            <Button
              onClick={() => handleSubmit()}
              // An assignment with no questions can never produce `hasChanges`,
              // which permanently locked learners out of a review_required
              // assignment (and therefore the rest of the course). Allow submit
              // when there is nothing to change but no submission exists yet.
              disabled={
                submitMutation.isPending ||
                (!isTopShop && !hasChanges && !canSubmitWithoutChanges)
              }
            >
              {submitMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isTopShop ? "Mark ready for scoring" : isSubmitted ? "Update" : "Submit"}
                </>
              )}
            </Button>
          </div>
          )}
        </div>
        <AlertDialog open={confirmNoEvidence} onOpenChange={setConfirmNoEvidence}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark ready without photos or links?</AlertDialogTitle>
              <AlertDialogDescription>
                You have not added any photos or links. The judges score this challenge using your evidence. Mark it ready anyway?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => { setConfirmNoEvidence(false); handleSubmit(true); }}>Mark ready for scoring</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AccordionContent>
    </AccordionItem>
  );
}

interface QuestionRendererProps {
  question: AssignmentQuestion;
  index: number;
  answer: string;
  fileUrl?: string;
  uploading?: boolean;
  onAnswerChange: (questionId: string, value: string) => void;
  onMultipleCorrectChange: (questionId: string, optionId: string, checked: boolean) => void;
  onFileUpload: (questionId: string, file: File) => void;
  onRemoveFile: (questionId: string) => void;
}

function QuestionRenderer({
  question,
  index,
  answer,
  fileUrl,
  uploading,
  onAnswerChange,
  onMultipleCorrectChange,
  onFileUpload,
  onRemoveFile,
  isMissingRequired,
}: QuestionRendererProps & { isMissingRequired?: boolean }) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-2">
      <Label htmlFor={`question-${question.id}`}>
        {index + 1}. {question.question_text}
        {question.is_required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {isMissingRequired && (
        <p className="text-sm text-destructive">This question is required.</p>
      )}
      
      {question.question_type === "textarea" && (
        <Textarea
          id={`question-${question.id}`}
          value={answer}
          onChange={(e) => onAnswerChange(question.id, e.target.value)}
          placeholder="Enter your answer..."
          rows={4}
        />
      )}
      
      {question.question_type === "number" && (
        <Input
          id={`question-${question.id}`}
          type="number"
          value={answer}
          onChange={(e) => onAnswerChange(question.id, e.target.value)}
          placeholder="Enter a number..."
        />
      )}
      
      {question.question_type === "text" && (
        <Input
          id={`question-${question.id}`}
          type="text"
          value={answer}
          onChange={(e) => onAnswerChange(question.id, e.target.value)}
          placeholder="Enter your answer..."
        />
      )}

      {(question.question_type === "multiple_choice" || question.question_type === "true_false") && question.options && (
        <RadioGroup
          value={answer}
          onValueChange={(value) => onAnswerChange(question.id, value)}
          className="space-y-2"
        >
          {question.options.map((option) => (
            <div key={option.id} className="flex items-center space-x-2">
              <RadioGroupItem value={option.id} id={`option-${option.id}`} />
              <Label htmlFor={`option-${option.id}`} className="font-normal cursor-pointer">
                {option.option_text}
              </Label>
            </div>
          ))}
        </RadioGroup>
      )}

      {question.question_type === "multiple_correct" && question.options && (
        <div className="space-y-2">
          {question.options.map((option) => {
            const selectedOptions = answer ? answer.split(",") : [];
            return (
              <div key={option.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`option-${option.id}`}
                  checked={selectedOptions.includes(option.id)}
                  onCheckedChange={(checked) => 
                    onMultipleCorrectChange(question.id, option.id, checked as boolean)
                  }
                />
                <Label htmlFor={`option-${option.id}`} className="font-normal cursor-pointer">
                  {option.option_text}
                </Label>
              </div>
            );
          })}
        </div>
      )}

      {question.question_type === "file_upload" && (
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.txt,.xls,.xlsx,.ppt,.pptx"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                onFileUpload(question.id, file);
              }
            }}
          />
          
          {fileUrl ? (
            <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
              <FileIcon className="h-5 w-5 text-primary" />
              <span className="flex-1 text-sm truncate">{answer}</span>
              <button
                type="button"
                onClick={async () => {
                  const failure = await openSignedStorageUrl("homework-files", fileUrl);
                  if (failure) toast.error(failure);
                }}
                className="text-xs text-primary hover:underline"
              >
                View
              </button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onRemoveFile(question.id)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File
                </>
              )}
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            Accepted formats: PDF, Word, Excel, PowerPoint, images, and text files
          </p>
        </div>
      )}
    </div>
  );
}
